import { defineMessage, type MessageDescriptor } from 'react-intl'

export type VenueInspirationOption = 'featured' | 'beachfront' | 'cityEscapes' | 'garden_winery' | 'intimateWeddings'

interface WeddingVenueMessages {
  location: MessageDescriptor
  title: MessageDescriptor
  guestCapacity: MessageDescriptor
  season: MessageDescriptor
  seasonHint?: MessageDescriptor
  description: MessageDescriptor
  highlights: Array<MessageDescriptor>
}

export interface WeddingVenueListItem {
  offerId: string
  messages: WeddingVenueMessages
  optionType: VenueInspirationOption
}

export const WEDDING_VENUES_LIST: Array<WeddingVenueListItem> = [
  {
    offerId: '006RF00000OWJcoYAH',
    messages: {
      location: defineMessage({
        id: 'vPzDH5KKQEb33h5yIwFx',
        defaultMessage: 'Vietnam',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'oRZUmyWWwaHtWclcEZYd',
        defaultMessage: 'Victoria Hoi An Beach Resort & Spa',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'x__-hcQvJadWh7Ao9fYH',
        defaultMessage: '~109 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '5KBTsFbeSwajKxpXnsfb',
        defaultMessage: 'Feb - Jul',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '96ABqXBK27wykXyQDShl',
        defaultMessage: 'Exchange vows on a secluded stretch of Cua Dai Beach as golden light fades into evening. Blending French colonial charm with the rich heritage of Hoi An, this resort offers a romantic, laid-back setting where every detail - from lantern-lit gardens to oceanfront ceremonies - is curated by a dedicated wedding planning team.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'bKvmVz1X4jHbW-3zXFd9',
          defaultMessage: 'Private beachfront ceremonies on Cua Dai Beach as the sun sets over the South China Sea',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '5cx5GyQEf4T4LXX1grth',
          defaultMessage: "Vietnamese, French and Japanese architectural influences throughout the resort's indoor spaces",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '2G47mDxDkBg2pGBJ87m3',
          defaultMessage: 'Lantern-lit garden pathways perfect for photography',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '006RF00000Yu52jYAB',
    messages: {
      location: defineMessage({
        id: 'ua6Eto7J7o1GtrxCY2sK',
        defaultMessage: 'Indonesia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'iwD1Jvpx9jhXaTkIqEcr',
        defaultMessage: 'Ayodya Resort Bali',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'bM1Lf7HQQM83lZVYjMPf',
        defaultMessage: '~537 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'pE-8b0oM72U7Y7_TSjN-',
        defaultMessage: 'Say "I do" on the beachfront of a genuine Balinese water palace, watched over by carved statues inspired by the Ramayana epic. From a torch-lit ceremony pavilion to a coconut grove reception, Ayodya blends grand architecture with warm Balinese tradition for a truly theatrical celebration.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'Bc6d3tSdvKQPK3aQ_vNH',
          defaultMessage: "Beachfront ceremony pavilion on one of Nusa Dua's most exclusive stretches of white sand",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '3nCgnD2lK3NLtPtvrBOP',
          defaultMessage: 'Traditional Balinese water-palace architecture with dramatic carved statues and cascading fountains',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'Yw3fv5E95h8XQMsDIfHX',
          defaultMessage: 'Reception in a fairy-light-lit private coconut grove, complete with Kecak dancers and fire performers',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '006RF00000OhlilYAB',
    messages: {
      location: defineMessage({
        id: 'ua6Eto7J7o1GtrxCY2sK',
        defaultMessage: 'Indonesia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'owcfvqM3DOztTIhlGo8T',
        defaultMessage: 'The Apurva Kempinski Bali',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ZSWvCFEYViiS7yH_lTeZ',
        defaultMessage: '~475 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'AO0PCkWnKd-cWFb5vyOL',
        defaultMessage: 'Perched atop the majestic cliffs of Nusa Dua, The Apurva Kempinski Bali is an open-air theatre of Indonesian elegance. Say your vows in the light-filled Apurva Chapel before dancing the night away on the Ocean Front Lawn, where the Indian Ocean provides an unforgettable backdrop to every moment.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: '8PNyi9GlIy8jDJ9_ZB4F',
          defaultMessage: 'Striking bamboo-and-glass Apurva Chapel perched atop the Nusa Dua cliffs',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '7W4YdtEq0a4fmzOGvNzT',
          defaultMessage: "Bali's largest garden beachfront wedding lawn, with panoramic Indian Ocean views",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '0Nv8aGkxJDBzELt9psOk',
          defaultMessage: 'Pillarless, naturally-lit Candi Ballroom for a show-stopping indoor reception',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '0062y000009BVOlAAO',
    messages: {
      location: defineMessage({
        id: 'Yl6Lf_XmmYgxqY2qzWxA',
        defaultMessage: 'Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '3mEkIkd4CUeESufDtBwV',
        defaultMessage: 'InterContinental Hayman Island Great Barrier Reef',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'AkJ_sMtyGE47mhzG6THK',
        defaultMessage: '~182 Rooms, Suites & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'WUmTj21etszSduizfAdt',
        defaultMessage: 'Dry season, cooler and less humid',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'tZv6pxt6OJP0YCiQfNrW',
        defaultMessage: 'Set within the heart of the Great Barrier Reef, InterContinental Hayman Great Barrier Reef is an iconic island wedding destination. Say your vows barefoot on the sand or from a hilltop chapel overlooking the Whitsundays, then celebrate under the stars.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: '7JJp2IHdTpDaby5Zc0ue',
          defaultMessage: 'Barefoot beachfront ceremonies on a private white-sand beach',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'NfwepYEjVZWInDXTwUzY',
          defaultMessage: 'Hilltop Stella Maris Chapel with sweeping views over the Whitsundays and Coral Sea',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'QjH7nXhiG1De4hSlFZwx',
          defaultMessage: 'Access to the Great Barrier Reef for unforgettable pre- or post-wedding experiences',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '006RF00000Xqyl4YAB',
    messages: {
      location: defineMessage({
        id: 'EvDYQ7nhr8Wnh2uupmzK',
        defaultMessage: 'Maldives',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'owPIKMIqJbUx8cIRltro',
        defaultMessage: 'Pullman Maldives Maamutaa',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ffCuUbnPuXtEvDkWS71e',
        defaultMessage: '~122 Villas & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'o3wP-dg5jlkI6-M-o7I1',
        defaultMessage: 'Nov - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'fcKalWRPSDuWo20nt2CC',
        defaultMessage: "Say \"I do\" on a private lagoon in the picture-perfect Gaafu Alifu Atoll, where overwater pavilions and secluded sandbanks set the scene for the ultimate intimate celebration. With underwater villas and personalised planning from Pullman's wedding team, every detail is designed for a truly private island escape.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'JeQ5imIG3LX7MseWxV-s',
          defaultMessage: 'Overwater wedding pavilion set against a private lagoon',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'aNmJCo7MX7q4-Zg-EgtN',
          defaultMessage: 'Rare underwater-bedroom Aqua Villas for an unforgettable honeymoon stay',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '2F9cdmxXQcHzmNPceecx',
          defaultMessage: 'Private deserted-island ceremony options for total seclusion',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'featured',
  },
  {
    offerId: '0062y000008gGL1AAM',
    messages: {
      location: defineMessage({
        id: '6YjtlTak6Opq8wTb2IhO',
        defaultMessage: 'Coral Coast, Fiji',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'LcFNinJ2kb265eE4veF2',
        defaultMessage: 'Warwick Fiji',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: '67F9-TlHSXVNzzo_tCb3',
        defaultMessage: '~248 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '9V9Kgqh98dqC3NrU_uuc',
        defaultMessage: 'May - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'axWkZ0YbaN52R2vAfPth',
        defaultMessage: 'Exchange vows on a private island fringed by the South Pacific, then dance the night away on the oceanfront Navau Deck as the Coral Coast glows in the evening light. With 28 acres of tropical gardens and experienced onsite wedding planners, Warwick Fiji delivers barefoot island romance at every turn.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'M0sbiGqWmEKJr6d440fP',
          defaultMessage: "Private island ceremonies on Wicked Walu, Warwick's own island restaurant",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'kYfWYwbhszMQvKoFNhjQ',
          defaultMessage: 'Purpose-built oceanfront Navau Deck for ceremonies and receptions under the stars',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'oXYylogwWbhuAuKy24j6',
          defaultMessage: '28 acres of lush tropical gardens for photography and outdoor celebrations',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '02b9e0d4-97df-4d7c-a0d4-334c51e7232d',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'FByqzcXFhiPQGVoxQqHp',
        defaultMessage: 'Phuket Marriott Resort & Spa, Merlin Beach',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ODOxIf03wWIWzsHkyRrh',
        defaultMessage: '~414 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'o3wP-dg5jlkI6-M-o7I1',
        defaultMessage: 'Nov - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'fMzXMZYkUb3Ia7Ur5fG4',
        defaultMessage: "Set on Phuket's secluded Tri-Trang Beach, this resort offers a sophisticated escape from the island's busier shores. Say your vows on a sweeping beachfront lawn as the Andaman Sea shimmers behind you, before celebrating long into the night in an elegant ballroom.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: '9X3G6n1Rrs5M8eYsMZDy',
          defaultMessage: 'Secluded setting on Tri-Trang Beach',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'PFIHZN6ha39SN48pyMvD',
          defaultMessage: 'Expansive beach lawn to accommodate both intimate and larger celebrations',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'Phac_gQXI7xpUdVC7ULT',
          defaultMessage: 'Contemporary Merlin Grand Ballroom for indoor events',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '0062y000006zXGKAA2',
    messages: {
      location: defineMessage({
        id: '6YjtlTak6Opq8wTb2IhO',
        defaultMessage: 'Coral Coast, Fiji',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '12VkzAkr9U1ZdhYLh8f8',
        defaultMessage: 'Shangri-La Yanuca Island, Fiji',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'mWYChGra6WjUdPgKet65',
        defaultMessage: '~443 Rooms, Suites & Bures',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '9V9Kgqh98dqC3NrU_uuc',
        defaultMessage: 'May - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'zZOddpJ3LCmHv5q1tpeJ',
        defaultMessage: 'Exchange vows in a glass-walled chapel overlooking the reef, then celebrate on a private island shaped by the rhythms of a traditional Fijian village. With turquoise lagoons on one side and the open Pacific on the other, Shangri-La Yanuca Island blends barefoot romance with polished five-star service.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: '5nHuEw5keOQeRzZBrbKP',
          defaultMessage: 'Non-denominational Seaside Chapel with glass walls overlooking the ocean and reef',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'CRsByfJGQCDEm0ogtUh-',
          defaultMessage: 'Located on a private 109-acre island connected to the mainland by a causeway',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'cv5axAPzcwU1GYykN67U',
          defaultMessage: 'Traditional Fijian village-inspired layout with lagoon and ocean-view accommodation',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '006RF00000OY7PRYA1',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'mVMDFJB0fkGvEXmY-Jyi',
        defaultMessage: 'Devasom Khao Lak Beach Resort & Villas',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'wjAkYXEPAu6bebYNjksL',
        defaultMessage: '~69 Rooms & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'o3wP-dg5jlkI6-M-o7I1',
        defaultMessage: 'Nov - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'dvGFmFrdwSJaC0-kRwtb',
        defaultMessage: "Tucked between a natural lagoon and a Khao Lak's beach, Devasom is a boutique escape built for intimate celebrations. Say your vows barefoot on the sand as a vintage boat bobs on the horizon, then celebrate in lantern-lit gardens designed to feel like a private world of your own.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'cfQxyZrgRE4XgSOT7N3z',
          defaultMessage: "Intimate beachfront ceremonies on one of Khao Lak's widest stretches of sand",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '7cvGI4h8RUlyErfKzJxA',
          defaultMessage: 'Unique architecture inspired by a lost 7th-century maritime civilisation',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'vTa-fGoQ5COszQV_DYUO',
          defaultMessage: 'Vintage Thai boat anchored offshore to backdrop photography',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '006RF00000Utey1YAB',
    messages: {
      location: defineMessage({
        id: 'ua6Eto7J7o1GtrxCY2sK',
        defaultMessage: 'Indonesia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'ifE0EM1X3v1OBSkgwLmc',
        defaultMessage: 'Merusaka Nusa Dua Bali',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'KNgiOpW5Q69ZER396nP8',
        defaultMessage: '~455 Rooms, Suites & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'UQSM9k5jDGkT16WWvGEr',
        defaultMessage: 'Say "I do" in a glass-walled chapel where the sky and Indian Ocean seem to merge into one. Inspired by the Seven Balinese Goddesses, Merusaka Nusa Dua pairs beachfront ceremony spaces with lush gardens for a setting as spiritually rich as it is romantic.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'sr_ITzt4kRFcYmcVAUHX',
          defaultMessage: 'Glass-walled chapel with uninterrupted Indian Ocean views',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'SM1LwXZtncEaNWmqWvSI',
          defaultMessage: "Direct access to Mengiat Beach, one of Nusa Dua's most beautiful stretches of white sand",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'VAtgmL3vWRP9J2U8ipuy',
          defaultMessage: 'Architecture inspired by the Seven Balinese Goddesses',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '006RF00000VKOfSYAX',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'UDwuvbWmcl8ca4UzAPof',
        defaultMessage: 'Katathani Phuket Beach Resort',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'cAbYnQpgmgph1Kf_5iPt',
        defaultMessage: '~518 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'o3wP-dg5jlkI6-M-o7I1',
        defaultMessage: 'Nov - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'qrRKyXZyjZ6QM4KhfwKa',
        defaultMessage: 'Set along an 850-metre stretch of pristine Kata Noi Beach, Katathani is a pioneer of destination weddings in Phuket. Walk the aisle on a picture-perfect beachfront lawn before moving into an open-air marquee reception. Planning is guided by a wedding team with years of experience turning big days into unforgettable ones.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'Zz82ptDaKvkWOo17Rwvx',
          defaultMessage: 'Beachfront lawn along an 850-metre stretch of Kata Noi Beach',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'nBkgq27gdxSybt8CrhHl',
          defaultMessage: 'Onsite wedding team with experience coordinating hundreds of celebrations',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'I162f4pe1YY5uCNET2Ka',
          defaultMessage: 'Dedicated wedding gallery for bridal preparations, located steps from the ceremony lawn',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'beachfront',
  },
  {
    offerId: '0062y000004UNTDAA4',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '47HkrgynoPFr4IZcHezl',
        defaultMessage: 'Banyan Tree Bangkok',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'p8V4LBaRU-HIsyxO29GU',
        defaultMessage: '~327 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '7q5z1Xdj5lAA1iIXtKrT',
        defaultMessage: 'Nov - Feb',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'k9cVz66cc1Krexxatpl3',
        defaultMessage: 'Cooler, drier months',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'dvUys5nIWykzoAYljZLF',
        defaultMessage: 'Say your vows high above the city lights at Vertigo, an iconic rooftop setting in Bangkok, or drift down the Chao Phraya River aboard a private cruise. Banyan Tree Bangkok turns a cosmopolitan skyline into the ultimate romantic backdrop for couples who want their wedding to feel larger than life.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: '7UbA1icahn_VzDGNlmH9',
          defaultMessage: 'Rooftop ceremonies at Vertigo, located 61 floors above the city with 360-degree skyline views',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'qKOnSZcZAquZW-i69qlP',
          defaultMessage: 'Option to celebrate with a cruise along the historic Chao Phraya River',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'TTyiJpztRQFj0BnXtDet',
          defaultMessage: 'Dramatic urban wedding backdrop',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: 'fdda3ba5-5e65-4650-8504-285ffa715ae6',
    messages: {
      location: defineMessage({
        id: 'T2nyIs00D_Da93Y4FrZU',
        defaultMessage: 'Japan',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'pAMsjhgxjJdffcfCjS7N',
        defaultMessage: 'Hyatt Regency Tokyo',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'Tjd4_7ipg-8ka1QTTSUX',
        defaultMessage: '~746 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'wAVELmJdb_9EjXm5YKjF',
        defaultMessage: 'Mar - May & Sep - Nov',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'EsflybYbcbIFgY4qkjlp',
        defaultMessage: 'Cherry blossom and autumn foliage seasons',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '73pZLTNtX0-ig__L_Vhe',
        defaultMessage: 'In the heart of Shinjuku, Hyatt Regency Tokyo offers couples the choice of refined Western chapel or traditional Shinto ceremony room - both with sweeping views of the Tokyo skyline. With an onsite bridal boutique and wedding coordinators, every detail is designed to feel effortless.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'GThGeRC-OSkRwEWE1kcg',
          defaultMessage: 'Onsite chapel and traditional Shinto ceremony room cater for Western or Japanese-style weddings',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'Rf6GsPdsVBrspTo93vy6',
          defaultMessage: 'Two penthouse-level sky banquet rooms with panoramic Shinjuku skyline views',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'ebyI5qzixE20TJX28Ed4',
          defaultMessage: 'Onsite bridal boutique with gowns from leading designers',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: '0062y000003IAJ9AAO',
    messages: {
      location: defineMessage({
        id: 'YVjNuwWo-qDAa7vJeymR',
        defaultMessage: 'Thailand',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'KoOnqUtNodXGTmxHKerm',
        defaultMessage: 'Siam Kempinski Hotel Bangkok',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'JyEwlVOzl0mYEvoZ7psB',
        defaultMessage: '~397 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '7q5z1Xdj5lAA1iIXtKrT',
        defaultMessage: 'Nov - Feb',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'k9cVz66cc1Krexxatpl3',
        defaultMessage: 'Cooler, drier months',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '5SZw1D3szvQ6vbh1Tr9O',
        defaultMessage: 'Set on the grounds of a former royal palace garden, Siam Kempinski Bangkok blends European grandeur with Thai artistry. Exchange vows on the fountain-lined Rotunda Lawn before moving into the pillarless Chadra Ballroom, where lotus motifs and lush greenery carry the romance of the gardens indoors.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'OnqfYvFJty8Kj3jCm4qW',
          defaultMessage: 'Rotunda Lawn ceremony space surrounded by pools, fountains and lush palm gardens',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '5MH49riFXE4KHBh6kRQT',
          defaultMessage: 'Located within former royal gardens of Sra Pathum Palace',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '2Qtc75K9e8KVUdNVfELE',
          defaultMessage: 'Pillarless 900sqm Chadra Ballroom for a grand reception',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: '9c3c14ab-1cd7-4d46-9bcd-45134c87355a',
    messages: {
      location: defineMessage({
        id: 'Yl6Lf_XmmYgxqY2qzWxA',
        defaultMessage: 'Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'oLBIFJEignG3yoRSAsks',
        defaultMessage: 'Crown Towers Sydney',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'jca9EHeYz0S2zVB609AD',
        defaultMessage: '~349 Rooms, Suites & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'CNUbpHB3YtBlXv43DUU4',
        defaultMessage: 'Sep - Nov & Mar - May',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'tMlxTUde0-olXCp95KgU',
        defaultMessage: 'Shoulder seasons',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'l5o20ltPh10vXCN5C-9y',
        defaultMessage: 'Say your vows with the Sydney Opera House and Harbour Bridge as your backdrop at Crown Towers Sydney. From the light-filled Pavilion to the glittering Pearl Ballroom, this Barangaroo landmark delivers a wedding as bold and unforgettable as the city skyline itself.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'brq4Ei3gqirafJZdc8k4',
          defaultMessage: 'Sweeping Sydney Harbour views from the Pavilion ceremony space, including the Sydney Opera House and Harbour Bridge',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'f3DBoITTSIZ-PuGfFqPJ',
          defaultMessage: 'Grand Pearl Ballroom with soaring ceilings and statement chandeliers for large receptions',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'bN1DZlCQJupl3PKvE3iN',
          defaultMessage: 'Architecturally iconic building in the heart of Barangaroo',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: '006RF00000WfEEIYA3',
    messages: {
      location: defineMessage({
        id: '4u9u5m8toEhfcfdfvJZ8',
        defaultMessage: 'Singapore',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'MnR3WiDXDcUh49KYZ0uF',
        defaultMessage: 'Amara Singapore',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ml8K7vLjfh27JdQBLSwM',
        defaultMessage: '~389 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '_Vl-SM95NtDCX8XsWcMj',
        defaultMessage: 'Feb - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'D6G_plJWs2zTusR5bAFL',
        defaultMessage: "Walk down a striking 38-metre aisle beneath a star-flecked ceiling in Amara Singapore's column-free Grand Ballroom. Bathed in natural light and set in the heart of the city's Central Business District, it's a polished, contemporary stage for couples who want big-city glamour without leaving downtown Singapore.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'qQDOJHaM5ff_ZgAbSWm_',
          defaultMessage: 'Column-free Grand Ballroom with a dramatic 38-metre march-in aisle',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'BV2100DyKyTeAT8FhYBm',
          defaultMessage: 'Floor-to-ceiling windows that fill the ballroom with natural light',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'jSWewbAxsSM3EoswrLtE',
          defaultMessage: "Located in central Tanjong Pagar, close to Singapore's heritage districts",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: 'c436c5c4-aa12-486b-84c4-8f08719c171e',
    messages: {
      location: defineMessage({
        id: '4u9u5m8toEhfcfdfvJZ8',
        defaultMessage: 'Singapore',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'BAuTwWtPNcOE4ae23mGA',
        defaultMessage: 'The Ritz-Carlton, Millenia Singapore',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'VcGWf7DMlrsQFISQL_Fc',
        defaultMessage: '~608 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '_Vl-SM95NtDCX8XsWcMj',
        defaultMessage: 'Feb - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '6Li8Widl8gzVkLKkFzuN',
        defaultMessage: "Walk a chandelier-lit, 35-metre aisle in one of Marina Bay's most celebrated ballrooms. With panoramic skyline views and a museum-worthy art collection throughout, The Ritz-Carlton, Millenia Singapore pairs old-world grandeur with the energy of one of Asia's most exciting cities.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'p_po3x7pTaTMSLIVXih1',
          defaultMessage: '35-metre aisle beneath glittering chandeliers in the Grand Ballroom',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'cW9JA2dH5IcXYH7jU2om',
          defaultMessage: 'Indoor and outdoor spaces to suit every celebration',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'nmaXCwSO7eHOKWQp1JfR',
          defaultMessage: 'Experience wedding team to make every dream come true',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'cityEscapes',
  },
  {
    offerId: '0062y000009ErHOAA0',
    messages: {
      location: defineMessage({
        id: 'DHvUWCSq6ULhECJ3UBec',
        defaultMessage: 'Mornington Peninsula, Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'y2fnl4beRdIQqbeykf-Z',
        defaultMessage: 'Lancemore Lindenderry Red Hill',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'BuGaFLS2NBZaOrOJuABS',
        defaultMessage: '~40 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'OLNpYVk2eixA_msVHpJs',
        defaultMessage: 'Oct - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'i2NG7P-jTjlEAs_BTKAN',
        defaultMessage: 'Spring and summer',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'FNofgeGVnrx-KmQblWz0',
        defaultMessage: "Say \"I do\" beside a mirror-still lake framed by silver birch trees, then walk a cypress-lined vineyard aisle at this boutique Mornington Peninsula estate. With ceremony, reception and accommodation all in one place, Lancemore Lindenderry Red Hill offers an elegant winery wedding just an hours' drive from Melbourne.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'A-hfjQw7ygoyl4VoMrVi',
          defaultMessage: 'Ceremony space overlooking a tranquil lake framed by silver birch trees',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'J0dxZ0uR-ANvKRF4tGJQ',
          defaultMessage: 'Vineyard aisle flanked by manicured cypress trees and rows of vines',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'J9noMekarRQYujCXgo-8',
          defaultMessage: 'Light-filled Lakeview reception space set within 34 acres of gardens and vineyards',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'garden_winery',
  },
  {
    offerId: '006RF00000JILW7YAP',
    messages: {
      location: defineMessage({
        id: 'DHvUWCSq6ULhECJ3UBec',
        defaultMessage: 'Mornington Peninsula, Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '9Zqx0OMYydf06MxkIcLW',
        defaultMessage: 'Jackalope Hotel',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ovYHBEgRT2y_aZ5JR9lX',
        defaultMessage: '~45 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'OLNpYVk2eixA_msVHpJs',
        defaultMessage: 'Oct - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'i2NG7P-jTjlEAs_BTKAN',
        defaultMessage: 'Spring and summer',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'z4xxEN5ks4OegArQGVAc',
        defaultMessage: "Take over one of Australia's most architectural hotels located next to Willow Creek Vineyard on Victoria's Mornington Peninsula. With a striking black infinity pool, gallery-worthy interiors and vineyard views at every turn, Jackalope offers couples a wedding weekend that feels as daring and design-led as the hotel itself.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'RFCYAyiynQ9Mw_1nJRqh',
          defaultMessage: 'Exclusive-use hotel buyouts to transform the whole property into a private wedding weekend',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 's6L_0gnOOHlHCpB2xQTp',
          defaultMessage: 'Terrace ceremonies overlooking Willow Creek Vineyard',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'NXWsHQIQ22A0jKGck4ms',
          defaultMessage: "Bold, art-filled interiors designed with some of Australia's leading creatives",
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'garden_winery',
  },
  {
    offerId: 'aa4ac49b-d0f9-4164-b306-9662ad8f96ac',
    messages: {
      location: defineMessage({
        id: 'uvnTdLZqIvE8xg_YLYb6',
        defaultMessage: 'Yarra Valley, Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'kJ_OYvcGLr8iLPWC6pSw',
        defaultMessage: 'Balgownie Estate',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'qBRZJvGHOF11UB05Bl7v',
        defaultMessage: '~70 Rooms',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'S69EK58qaN1Pf63ylfeq',
        defaultMessage: 'Sep - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'i2NG7P-jTjlEAs_BTKAN',
        defaultMessage: 'Spring and summer',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'jSQnNu8g-PktOpb47k4F',
        defaultMessage: "Exchange vows among rolling vineyards just an hours' drive from Melbourne, then raise a glass of the estate's acclaimed wine as the sun sets over the Yarra Ranges. Able to host everything from ceremony and reception to spa appointments and accommodation Balgownie Estate makes for an all-in-one wine country wedding.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'aVMSNL6kNe_fMW9aiLoo',
          defaultMessage: 'Multiple ceremony and reception locations across 75 acres of vineyards',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'kZC9i73NCZDkxlhgINxj',
          defaultMessage: 'Award-winning wines served at the reception',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'qPOPZsPcoPa5A00E0Ymk',
          defaultMessage: 'Onsite spa, restaurant and accommodation available for create a wedding weekend',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'garden_winery',
  },
  {
    offerId: 'b309d5a8-2d1e-44ab-895c-524bd38a930f',
    messages: {
      location: defineMessage({
        id: 'wb7JTpmh9s43So9eE4oz',
        defaultMessage: 'Mekong Delta, Vietnam',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '3SwRq70obqBhMrsvMSIs',
        defaultMessage: 'Victoria Can Tho Resort',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'X39Q6XkET4F2kATh6e44',
        defaultMessage: '~92 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '-eKSdis7xI2koOHO67MW',
        defaultMessage: 'Dec - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '2H1mCdZlMbih1lSqTn81',
        defaultMessage: 'Set on the banks of the Hau River in the heart of the Mekong Delta, Victoria Can Tho blends old-world elegance with authentic riverside charm. Exchange your vows in a lush riverside garden before celebrating with a sunset cruise on a traditional rice barge - an intimate wedding infused with the rhythm of the Mekong.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'z6l3xuoD5pmgUHegYrzv',
          defaultMessage: 'Riverside garden ceremonies overlooking the Hau River',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'oADVSbqhxkPi-YWgXBst',
          defaultMessage: 'Elegant Indochine architecture blending French colonial and Vietnamese heritage',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'u5e56Sz8thUJ7UT31cB4',
          defaultMessage: 'Sunset cruises on a traditional wooden rice barge for a local touch',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
  {
    offerId: '006RF00000TWd2MYAT',
    messages: {
      location: defineMessage({
        id: 'TjzOj8C0wbJtouxGTmo7',
        defaultMessage: 'Hunter Valley, Australia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'tmanSPKsA9fj5dsb5U8Y',
        defaultMessage: 'Chateau Elan at The Vintage',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'HV3hAivtrCwTEdWjeym3',
        defaultMessage: '~100 Rooms & Suites',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'wAVELmJdb_9EjXm5YKjF',
        defaultMessage: 'Mar - May & Sep - Nov',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'le-UgOI6-9-FcN76ywst',
        defaultMessage: 'Mild seasons',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'RDC7tYOxL9LUof8UUJr9',
        defaultMessage: "Exchange vows in a handcrafted sandstone chapel shaded by gum trees, with Hunter Valley vineyards rolling out beyond. Chateau Elan pairs old-world charm with modern indulgence: an award-winning spa, a championship golf course and elegant event spaces make it a wedding destination guests won't want to leave.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'zWgtfG8x0dDnEV5aeYHR',
          defaultMessage: 'Handbuilt, convict-cut sandstone Carriage House chapel shaded by gum trees',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'z07tA7m3mNCH8Cg2LFnX',
          defaultMessage: 'Ceremony sites overlooking a Greg Norman-designed golf course and rolling vineyards',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'eZAx21xcos6w3V4OEXcP',
          defaultMessage: 'Onsite Hunter Valley Day Spa with 17 treatment rooms for pre-wedding pampering',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
  {
    offerId: '006RF00000Wh2owYAB',
    messages: {
      location: defineMessage({
        id: 'AGgUNiQ5Bdl3iVJpFbbO',
        defaultMessage: 'Bali, Indonesia',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'IV6sNnQ12PL7tdpxMU0R',
        defaultMessage: 'Kappa Senses Ubud',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'gFv5ed8n2vriL5gR9_qE',
        defaultMessage: '~76 Rooms & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'Hobao1SdsGMk1lO6XKhb',
        defaultMessage: 'Say your vows surrounded by emerald rice paddies at this eco-conscious French-designed retreat in Ubud. Inspired by the Balinese legend of Rama and Shinta, Kappa Senses blends romance, sustainability and quiet luxury for couples seeking an intimate celebration close to nature.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'Irq9NQaCPYCGgIHDA2TZ',
          defaultMessage: 'Ceremonies set amid vibrant rice paddies, inspired by the tale of Rama and Shinta',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '0QIfdwskisYAKxfIapwy',
          defaultMessage: 'Eco-conscious, French-designed resort with a working permaculture garden',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'DjSXzYESWv-sIf7rKGE8',
          defaultMessage: 'OmTara Spa by Clarins for indulgent pre-wedding pampering',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
  {
    offerId: '006RF00000VPN1BYAX',
    messages: {
      location: defineMessage({
        id: '8x3l-cbi1eSOgoU5zGMl',
        defaultMessage: 'Ubud, Bali',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: '-9AVH7Cacz3OsrpTSl0V',
        defaultMessage: 'Tanah Gajah, a Resort by Hadiprana',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ScGpI57lkF4_crYD4wkL',
        defaultMessage: '~24 Suites & Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: 'XeZDsvPcsuSYg3OQr0rJ',
        defaultMessage: 'Apr - Oct',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: '8J0oifxaVQXGNQ4MMT3G',
        defaultMessage: 'Say your vows beneath the historic Elephant Gate, once part of the private Hadiprana family estate. Set across six hectares of rice paddies and gardens, Tanah Gajah offers couples an intimate, art-filled sanctuary. Options to buy out the entire resort for family and friends are available.',
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'zlY4QyF2ctjBDlletnNq',
          defaultMessage: 'Ceremonies beneath the historic Elephant Gate, a remnant of the original Hadiprana family estate',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'TeGk8QUVJ42ZPCESTZgF',
          defaultMessage: 'Full resort buy-outs across a six-hectare estate of rice paddies and gardens available',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '0BWXIR6qwgwcOxi-gTy2',
          defaultMessage: 'Curated collection of original Balinese art throughout the property',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
  {
    offerId: '006RF00000NuworYAB',
    messages: {
      location: defineMessage({
        id: 'EvDYQ7nhr8Wnh2uupmzK',
        defaultMessage: 'Maldives',
        description: 'Location of a venue inspiration on the weddings landing page.',
      }),
      title: defineMessage({
        id: 'h8N3ielW4PiX2PR0VCMA',
        defaultMessage: 'Park Hyatt Maldives Hadahaa',
        description: 'Title of a venue inspiration on the weddings landing page.',
      }),
      guestCapacity: defineMessage({
        id: 'ly4aVlqYy3gbilBuQjdG',
        defaultMessage: '~51 Villas',
        description: 'Guest capacity of a venue inspiration on the weddings landing page.',
      }),
      season: defineMessage({
        id: '-eKSdis7xI2koOHO67MW',
        defaultMessage: 'Dec - Apr',
        description: 'Best season of a venue inspiration on the weddings landing page.',
      }),
      seasonHint: defineMessage({
        id: 'bkxiY5vl8sZH85PKLqvl',
        defaultMessage: 'Dry season',
        description: 'Season detail tooltip for a venue inspiration on the weddings landing page.',
      }),
      description: defineMessage({
        id: 'RVouADDlf5VrNy32KITO',
        defaultMessage: "Exchange vows on a remote private island encircled by one of the Maldives' most pristine coral reefs. With just 51 minimalist villas set between beach and lagoon, Park Hyatt Maldives Hadahaa offers barefoot, unhurried romance far from the crowds.",
        description: 'Description of a venue inspiration on the weddings landing page.',
      }),
      highlights: [
        defineMessage({
          id: 'HnzIj2P-rodsE0JTs7sA',
          defaultMessage: 'Pristine 360-degree house reef, just steps from shore',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: '8nAc7w9nEpyXf8-PSjQF',
          defaultMessage: 'Overwater and beach villas designed with Maldivian timber and stone',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
        defineMessage({
          id: 'gDljneWiQgP9SlQA1zcU',
          defaultMessage: 'One of the most remote and private islands in the Maldives',
          description: 'Venue inspiration highlight on the weddings landing page.',
        }),
      ],
    },
    optionType: 'intimateWeddings',
  },
]
